import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PermitStep, PhotoRequirement } from "@ha/data/basic";
import { HaModalService, LanguageStateSelectors } from "@ha/ui/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Navigate } from "@ngxs/router-plugin";
import { Actions, ofActionCompleted, Select, Store } from "@ngxs/store";
import { combineLatest, filter, map, Observable, pairwise, startWith } from "rxjs";
import { PermitDetailsActions } from "../../../permit-details/states/permit-details.action";
import { PermitDetailsSelectors } from "../../../permit-details/states/permit-details.selectors";
import { PermitPaths } from "../../../permit-paths";
import { CurrentStep } from "../../../permit/models/current-step.model";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";
import { PermitStepState, PermitStepStateModel } from "../../../permit/states/permit-step.state";
import { CustomerPermitTemplate } from "../../models/customer-permit-template.model";
import { FixedTemplate, Question as FixedTemplateQuestion } from "../../models/fixed-template.model";
import { Permit } from "../../models/permit.model";
import { PermitPhoto } from "../../models/permit-photo.model";
import { FixedQuestion } from "../../models/question.model";
import { WorkplaceDetails } from "../../models/workplace-details.model";
import { PermitCreateActions } from "../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";
import { PermitPhotoSelectors } from "../../states/permit-photo-selectors";
import { PermitPhotoActions } from "../../states/permit-photo.actions";
import { PermitQuestionActions } from "../../states/permit-question.actions";
import { PermitQuestionSelectors } from "../../states/permit-question.selectors";
import { PermitCommentsDto } from "../../../permit-details/models/permit-comments-dto";
import { PermitPhotoCommentModalComponent } from "../permit-photo-comment-modal/permit-photo-comment-modal.component";

@UntilDestroy()
@Component({
    selector: "pmt-permit-create",
    templateUrl: "permit-create.component.html",
    styleUrls: ["permit-create.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitCreateComponent implements OnInit {
    private hasAutoAdvancedFromFlammableAtexCreated = false;

    @Input()
    public permitId: number | undefined;

    @Select(PermitStepSelectors.currentStep)
    public currentStep$: Observable<CurrentStep>;

    @Select(PermitCreateSelectors.fixedTemplateDetails)
    public fixedTemplate$: Observable<FixedTemplate>;

    @Select(PermitCreateSelectors.customerPermitTemplate)
    public customerTemplate$: Observable<CustomerPermitTemplate>;

    @Select(PermitCreateSelectors.workplaceDetails)
    public workplaceDetails$: Observable<WorkplaceDetails>;

    @Select(PermitCreateSelectors.permit)
    public permit$: Observable<Permit>;

    @Select(PermitStepSelectors.currentStep)
    public currentStepForCounts$: Observable<CurrentStep>;

    @Select(PermitQuestionSelectors.fixedQuestion)
    public currentFixedQuestionForCounts$: Observable<FixedQuestion | undefined>;

    @Select(PermitPhotoSelectors.permitPhotos)
    public photos$: Observable<PermitPhoto[]>;

    @Select(PermitDetailsSelectors.comments)
    public comments$: Observable<PermitCommentsDto>;

    public photoCount$: Observable<number>;
    public commentCount$: Observable<number>;

    public PermitStep = PermitStep;
    public PhotoRequirement = PhotoRequirement;


    constructor(
        private store: Store,
        private activatedRoute: ActivatedRoute,
        private modalService: HaModalService,
        private actions$: Actions,
    ) {}

    public ngOnInit(): void {
        this.store.dispatch(new PermitCreateActions.GetActiveFixedTemplate());

        // Initialize count observables
        this.photoCount$ = combineLatest([
            this.currentStepForCounts$,
            this.currentFixedQuestionForCounts$,
            this.photos$,
        ]).pipe(map(([currentStep, currentFixedQuestion, photos]) => {
            if (!photos || !currentStep) {
                return 0;
            }
            if (currentFixedQuestion?.id !== undefined && currentFixedQuestion?.id !== null) {
                return photos.filter((photo) =>
                    photo.step === currentStep.step && photo.questionId === currentFixedQuestion.id
                ).length;
            }
            return photos.filter((photo) => photo.step === currentStep.step).length;
        }));

        this.commentCount$ = combineLatest([
            this.currentStepForCounts$,
            this.currentFixedQuestionForCounts$,
            this.comments$,
        ]).pipe(map(([currentStep, currentFixedQuestion, comments]) => {
            if (!comments?.comments || !currentStep) {
                return 0;
            }
            if (currentFixedQuestion?.id !== undefined && currentFixedQuestion?.id !== null) {
                return comments.comments.filter((comment) =>
                    comment.step === currentStep.step && comment.questionId === currentFixedQuestion.id
                ).length;
            }
            return comments.comments.filter((comment) => comment.step === currentStep.step).length;
        }));

        this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe((params) => {
            const permitId = parseInt(params.permitId as string);
            if (isNaN(permitId) || !params.permitId) {
                this.store.dispatch(new Navigate([PermitPaths.Root]));
                return;
            }

            this.hasAutoAdvancedFromFlammableAtexCreated = false;
        });

        this.store.select(PermitStepState).pipe(
            untilDestroyed(this),
            startWith(undefined),
            pairwise(),
            filter(([previousState, currentState]: PermitStepStateModel[]) => {
                // Cancel if no permit id
                if (!currentState.permitId) {
                    return false;
                }

                // If previous state was empty we want to get permit
                if (!previousState) {
                    return true;
                }

                // If permit id have changed we want to get permit
                if (currentState.permitId !== previousState.permitId) {
                    return true;
                }

                return false;
            }),
        ).subscribe(([, currentSate]: PermitStepStateModel[]) => {
            const authorize = this.store.selectSnapshot(PermitStepSelectors.currentStep).step >= 200;
            this.store.dispatch(new PermitCreateActions.GetPermit(currentSate.permitId, authorize));

            // Fetch photos, photo requirements, and comments for buttons
            this.store.dispatch(new PermitPhotoActions.GetPermitPhotoRequirement());
            const currentLanguage = this.store.selectSnapshot(LanguageStateSelectors.currentLanguage);
            this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(currentLanguage));

            const currentStep = this.store.selectSnapshot(PermitStepSelectors.currentStep);
            const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
            this.store.dispatch(
                new PermitDetailsActions.GetComments(
                    currentSate.permitId,
                    currentLanguage,
                    currentStep.step,
                    currentFixedQuestion?.id,
                ),
            );
        });

        // Listen for comment and photo actions to refresh counts
        this.actions$.pipe(
            ofActionCompleted(
                PermitDetailsActions.CreateComment,
                PermitDetailsActions.RemoveComment,
                PermitPhotoActions.SavePermitPhoto,
                PermitPhotoActions.RemovePermitPhoto,
            ),
            untilDestroyed(this),
        ).subscribe(() => {
            this.refreshCurrentStepData();
        });

        // Listen for navigation actions to refresh step data
        this.actions$.pipe(
            ofActionCompleted(
                PermitStepActions.GoForward,
                PermitStepActions.GoBackwards,
                PermitStepActions.GetCurrentStep,
                PermitQuestionActions.GetCurrentQuestion,
                PermitQuestionActions.GetNextQuestion,
                PermitQuestionActions.GetPreviousQuestion,
            ),
            untilDestroyed(this),
        ).subscribe(() => {
            this.refreshCurrentStepData();
        });

        this.currentStep$.pipe(
            untilDestroyed(this),
            filter((currentStep) => currentStep?.step === PermitStep.flammableAtexPermitCreated),
        ).subscribe(() => {
            const permitId = this.store.selectSnapshot(PermitStepSelectors.permitId) ?? this.permitId;
            if (this.hasAutoAdvancedFromFlammableAtexCreated || !permitId) {
                return;
            }

            this.hasAutoAdvancedFromFlammableAtexCreated = true;
            this.store.dispatch(new PermitStepActions.GoForward(permitId));
        });
    }

    public openModal(modalType: "photo" | "comment"): void {
        const currentStep = this.store.selectSnapshot(PermitStepSelectors.currentStep);
        const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
        this.modalService.open(PermitPhotoCommentModalComponent, true, {
            permitId: this.permitId,
            modalType,
            step: currentStep.step,
            questionId: currentFixedQuestion?.id,
        });
    }

    public isPhotoButtonVisible(): boolean {
        const isDemo = this.store.selectSnapshot(PermitCreateSelectors.isDemo);
        if (isDemo) {
            return false;
        }

        const currentStep = this.store.selectSnapshot(PermitStepSelectors.currentStep);
        const fixedTemplate = this.store.selectSnapshot(PermitCreateSelectors.fixedTemplateDetails);
        const currentPermit = this.store.selectSnapshot(PermitCreateSelectors.permit);

        if (
            currentPermit?.photoRequirement !== undefined
            && currentPermit?.photoRequirement === PhotoRequirement.Prohibited
        ) {
            return false;
        }

        switch (currentStep.step) {
            case PermitStep.isFlammableHotWork:
            case PermitStep.isWorkplaceTemporary:
                return true;
            case PermitStep.emergencyLocation:
                return fixedTemplate?.emergencyLocation?.photo ?? false;
            case PermitStep.flammableAtexQuestion: {
                const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
                return (this.getCurrentQuestionSettings(fixedTemplate)?.photo
                    && currentFixedQuestion?.customerComment?.allowPhotos !== false) ?? false;
            }
            case PermitStep.workMethod:
                return fixedTemplate?.workMethodsSettings?.photo ?? false;
            case PermitStep.additionalInformation:
                return false;
            case PermitStep.fireAlarm:
                return fixedTemplate?.fireAlarm?.photo ?? false;
            case PermitStep.preparationFixedQuestions:
            case PermitStep.fixedQuestions:
            case PermitStep.flammableMaterialsFixedQuestions:
            case PermitStep.atexFixedQuestions:
            case PermitStep.gasFreeCertificatesFixedQuestions:
            case PermitStep.safetyEquipmentFixedQuestions: {
                const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
                return (this.getCurrentQuestionSettings(fixedTemplate)?.photo
                    && currentFixedQuestion?.customerComment?.allowPhotos !== false) ?? false;
            }
            case PermitStep.preparationCustomerCheckpoints:
            case PermitStep.customerCheckpoints: {
                const currentCustomerQuestion = this.store.selectSnapshot(PermitQuestionSelectors.customerQuestion);
                return currentCustomerQuestion?.allowPhotos ?? false;
            }
            default:
                return false;
        }
    }

    public isCommentButtonVisible(): boolean {
        const isDemo = this.store.selectSnapshot(PermitCreateSelectors.isDemo);
        if (isDemo) {
            return false;
        }

        const currentStep = this.store.selectSnapshot(PermitStepSelectors.currentStep);
        const fixedTemplate = this.store.selectSnapshot(PermitCreateSelectors.fixedTemplateDetails);

        switch (currentStep.step) {
            case PermitStep.workplace:
                return false;
            case PermitStep.emergencyLocation:
                return fixedTemplate?.emergencyLocation?.comment ?? false;
            case PermitStep.flammableAtexQuestion: {
                const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
                return (this.getCurrentQuestionSettings(fixedTemplate)?.comment
                    && currentFixedQuestion?.customerComment?.allowComments !== false) ?? false;
            }
            case PermitStep.workMethod:
                return fixedTemplate?.workMethodsSettings?.comment ?? false;
            case PermitStep.additionalInformation:
                return false;
            case PermitStep.fireAlarm:
                return fixedTemplate?.fireAlarm?.comment ?? false;
            case PermitStep.preparationFixedQuestions:
            case PermitStep.fixedQuestions:
            case PermitStep.flammableMaterialsFixedQuestions:
            case PermitStep.atexFixedQuestions:
            case PermitStep.gasFreeCertificatesFixedQuestions:
            case PermitStep.safetyEquipmentFixedQuestions: {
                const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
                return (this.getCurrentQuestionSettings(fixedTemplate)?.comment
                    && currentFixedQuestion?.customerComment?.allowComments !== false) ?? false;
            }
            case PermitStep.preparationCustomerCheckpoints:
            case PermitStep.customerCheckpoints: {
                const currentCustomerQuestion = this.store.selectSnapshot(PermitQuestionSelectors.customerQuestion);
                return currentCustomerQuestion?.allowComments ?? false;
            }
            default:
                return true;
        }
    }

    private refreshCurrentStepData(): void {
        const permitId = this.store.selectSnapshot(PermitStepSelectors.permitId);
        if (!permitId) {
            return;
        }

        const currentStep = this.store.selectSnapshot(PermitStepSelectors.currentStep);
        const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);

        // Refresh photos and comments for current step
        const currentLanguage = this.store.selectSnapshot(LanguageStateSelectors.currentLanguage);
        this.store.dispatch(new PermitPhotoActions.GetPermitPhotos(currentLanguage));
        this.store.dispatch(
            new PermitDetailsActions.GetComments(permitId, currentLanguage, currentStep.step, currentFixedQuestion?.id),
        );
    }

    private getCurrentQuestionSettings(fixedTemplate: FixedTemplate | undefined): FixedTemplateQuestion | undefined {
        const currentFixedQuestion = this.store.selectSnapshot(PermitQuestionSelectors.fixedQuestion);
        if (!fixedTemplate || !currentFixedQuestion) {
            return undefined;
        }

        return [
            ...(fixedTemplate.questions ?? []),
            ...(fixedTemplate.flammableMaterialsQuestions ?? []),
            ...(fixedTemplate.atexQuestions ?? []),
            ...(fixedTemplate.safetyEquipmentQuestions ?? []),
            ...(fixedTemplate.gasFreeCertQuestions ?? []),
            ...(fixedTemplate.flammableAtexQuestion ? [fixedTemplate.flammableAtexQuestion] : []),
        ].find((question) => question.id === currentFixedQuestion.id);
    }
}
